import React from 'react';
import { Loader } from '../../components/Loader/Loader';
import Modal from '../../components/modals/Modal';
import { ReqEnum } from '../../const/requestStatus';
import { fetchAccounts } from '../../fetch/fetchUsers';
import {
  isOrganisationNumberValid,
  supportedCountries,
} from '../../utils/country';
import {
  fetchCompanyName,
  getOrgNumberStringAsString,
} from '../../utils/enhetsregisteretUtil';
import { Account, CourierTravelMode, User } from '../../types/userTypes';
import {
  AccountsChooseOrCreateRadioButtons,
  IsCompanyInAccountsList,
  IsCompanyInTestAccountsList,
  ShowTestAccountIfItExists,
} from '../create-user/CreateUser';
import { AccountRow } from './AccountRow';
import {
  EditUser,
  EmailRow,
  EmailVerified,
  ErrorMsg,
  RoleRow,
} from './EditAdminUserModal';
import './EditUserModal.scss';
import { LockUser } from './LockUser';
import { postUpdateUser } from './postUpdateUser';
import {
  IsThereAnyErrors,
  validateCourierFields,
  validateFields,
} from './validateFields';
import { sendPasswordResetLink } from './sendPasswordResetLink';
import { EditFormGroupModal } from './EditFormGroupModal';

const EDIT_USER_MODIFIED_CLASS = 'edit-user-modal__modified';
const EDIT_USER_FORM_LABEL_CLASS = 'edit-user-modal__form-label';

enum KindOfAccountSelection {
  CREATE,
  CHOOSE,
}

interface State {
  user: EditUser;
  userUpdated: boolean;
  updatingUser: boolean;
  fieldErrors: {
    [key: string]: ErrorMsg | undefined;
  };
  errors: ErrorMsg[];
  fetchingAccounts: boolean;
  accounts: Account[];
  accountIdSelected?: number | null;
  companyNameFromFetch: string;
  reqCompanyNameFromFetch: ReqEnum;
  isCompanyInTestAccountsList: boolean;
  isCompanyInAccountsList: boolean;
  kindOfAccountSelection: KindOfAccountSelection;
  resettingPassword: boolean;
}

interface Props {
  onCloseModal: (updated: boolean) => void;
  user: User;
}

export class EditSenderUserModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      userUpdated: false,
      updatingUser: false,
      user: {
        ...props.user,
        newEmail: '',
        companyName: props.user.companyName || '',
        companyNumber: props.user.companyNumber || '',
        paymentByInvoiceAllowed: props.user.paymentByInvoiceAllowed,
        travelMode: props.user.travelMode || CourierTravelMode.None,
        etaOffsetSeconds: props.user.etaOffsetSeconds || 0,
        etaOffsetSecondsInput: (props.user.etaOffsetSeconds || 0).toString(),
        regions: props.user.regions || [],
        createAccount: false,
      },

      accounts: [],
      accountIdSelected: null,
      fetchingAccounts: true,

      fieldErrors: {},
      errors: [],
      companyNameFromFetch: props.user.companyName,
      reqCompanyNameFromFetch:
        props.user.companyName && props.user.companyName.length > 0
          ? ReqEnum.SUCCESS
          : ReqEnum.PENDING,

      isCompanyInTestAccountsList: false,
      isCompanyInAccountsList: false,
      kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
      resettingPassword: false,
    };

    this.escFunction = this.escFunction.bind(this);
    this.updateUserIsLockedMethod = this.updateUserIsLockedMethod.bind(this);
  }

  updateUserIsLockedMethod(checked: boolean): void {
    this.updateUser({
      isLocked: checked,
    });
  }

  updateUser = (updatedUserFields: Partial<EditUser>): void => {
    const { user } = this.state;
    const updatedUser = { ...user, ...updatedUserFields };
    this.setState({ user: updatedUser, userUpdated: false });

    if (IsThereAnyErrors(this.state.fieldErrors)) {
      const fieldErrors = validateFields(updatedUser);
      this.setState({ fieldErrors: fieldErrors });
    }
  };

  updateUserByAccount = async (
    updatedUserFields: Partial<EditUser>
  ): Promise<void> => {
    const { user } = this.state;
    const { companyNumber } = updatedUserFields;

    const updatedUser = { ...user, ...updatedUserFields };
    this.setState({ user: updatedUser, userUpdated: false });

    if (companyNumber) {
      await this.getCompanyName(companyNumber);
    }

    const fieldErrors = validateCourierFields(
      updatedUser,
      this.state.reqCompanyNameFromFetch
    );

    if (IsThereAnyErrors(fieldErrors)) {
      this.setState({ fieldErrors: fieldErrors });
    }
  };

  escFunction(event: { keyCode: number }): void {
    if (event.keyCode === 27) {
      this.props.onCloseModal(this.state.userUpdated);
    }
  }

  async componentDidMount(): Promise<void> {
    document.addEventListener('keydown', this.escFunction, false);

    await this.getCompanyName(this.state.user.companyNumber);

    const userAccountId = this.props.user.accountId;
    const accounts = ((await fetchAccounts()) || []).filter(
      acc => acc.enabled || userAccountId === acc.id
    );
    this.setState({
      fetchingAccounts: false,
      accounts: accounts,
      accountIdSelected: this.props.user.accountId,
    });
  }

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.escFunction, false);
  }

  async saveUser(e: { preventDefault: () => void }): Promise<void> {
    e.preventDefault();
    const { user } = this.state;
    const validatedFieldErrors = validateFields(user);
    this.setState({ fieldErrors: validatedFieldErrors });

    if (IsThereAnyErrors(validatedFieldErrors)) return;

    this.setState({
      userUpdated: false,
      errors: [],
      updatingUser: true,
    });
    const result = await postUpdateUser(user, this.props.user.emailVerified);

    this.setState({
      userUpdated: !!result.success,
      errors: result.success ? [] : result.errors,
      updatingUser: false,
    });
  }

  async sendPasswordReset(e: React.MouseEvent): Promise<void> {
    e.preventDefault();
    this.setState({ resettingPassword: true });
    const { user } = this.state;
    const linkSent = await sendPasswordResetLink(user.email);
    if (linkSent) {
      this.props.onCloseModal(false);
    } else {
      this.setState({ resettingPassword: false });
    }
  }

  async getCompanyName(orgnumberAsString: string): Promise<void> {
    const currentOrgNumber: string =
      getOrgNumberStringAsString(orgnumberAsString);

    this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
    if (isOrganisationNumberValid(currentOrgNumber, this.state.user.country)) {
      const { companyNameFromFetch, status } = await fetchCompanyName(
        currentOrgNumber,
        this.state.user.country
      );

      if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() !== '404'
      ) {
        this.setState({ companyNameFromFetch: companyNameFromFetch });
        this.setState({ reqCompanyNameFromFetch: ReqEnum.SUCCESS });
        this.setState({
          isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
          isCompanyInAccountsList: IsCompanyInAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
        });
      } else if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() === '500'
      )
        this.setState({ reqCompanyNameFromFetch: ReqEnum.ERROR });
      else {
        this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      }
    } else {
      this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      this.setState({ isCompanyInTestAccountsList: false });
    }
  }

  render(): JSX.Element {
    const width = 800;

    const {
      user,
      errors,
      fieldErrors,
      userUpdated,
      updatingUser,
      accountIdSelected,
      fetchingAccounts,
      resettingPassword,
    } = this.state;
    const account = accountIdSelected
      ? this.state.accounts.find(acc => acc.id === accountIdSelected)
      : null;

    const saveButton = {
      title: 'Save',
      disabled: updatingUser,
      onClick: (e: any) => {
        this.saveUser(e);
      },
    };

    const resetPasswordButton = {
      title: 'Reset password',
      disabled: resettingPassword,
      onClick: (e: React.MouseEvent) => {
        this.sendPasswordReset(e);
      },
    };

    const buttons = userUpdated
      ? [resetPasswordButton]
      : [resetPasswordButton, saveButton];

    return (
      <Modal
        title="Edit user"
        onClose={() => this.props.onCloseModal(userUpdated)}
        isLoading={false}
        width={width}
        buttons={buttons}
      >
        <div className="edit-user-modal">
          <form className="edit-user-modal__form" autoComplete="off">
            <RoleRow role={user.role} />
            <EmailRow email={user.email} />
            <EmailVerified
              emailVerified={user.emailVerified}
              onChange={(event: { target: { checked: any } }) =>
                this.updateUser({
                  emailVerified: event.target.checked,
                })
              }
            />

            <EditFormGroupModal hasError={!!fieldErrors.newEmail}>
              <label>
                <span className="edit-user-modal__form-label">New Email</span>
                <input
                  type="email"
                  data-lpignore="true"
                  placeholder="new-email@domain"
                  name="emailNew"
                  value={user.newEmail}
                  onChange={event =>
                    this.updateUser({ newEmail: event.target.value })
                  }
                  className={
                    user.newEmail && user.newEmail !== this.props.user.email
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.newEmail && <em>{fieldErrors.newEmail.message}</em>}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.firstName}>
              <label>
                <span className="edit-user-modal__form-label">First name</span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder="Name"
                  name="firstName"
                  value={user.firstName}
                  onChange={event =>
                    this.updateUser({ firstName: event.target.value })
                  }
                  className={
                    user.firstName !== this.props.user.firstName
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.firstName && (
                <em>{fieldErrors.firstName.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.lastName}>
              <label>
                <span className="edit-user-modal__form-label">Last name</span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder="Last name"
                  name="lastName"
                  value={user.lastName}
                  onChange={event =>
                    this.updateUser({ lastName: event.target.value })
                  }
                  className={
                    user.lastName !== this.props.user.lastName
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.lastName && <em>{fieldErrors.lastName.message}</em>}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.phoneCode}>
              <label>
                <span className="edit-user-modal__form-label">
                  Phone country code
                </span>
                <input
                  type="tel"
                  data-lpignore="true"
                  placeholder="+47"
                  name="phoneCode"
                  value={user.phoneCode}
                  onChange={event =>
                    this.updateUser({ phoneCode: event.target.value })
                  }
                  className={
                    user.phoneCode !== this.props.user.phoneCode
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.phoneCode && (
                <em>{fieldErrors.phoneCode.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.phoneNumber}>
              <label>
                <span className="edit-user-modal__form-label">
                  Phone number
                </span>
                <input
                  type="tel"
                  data-lpignore="true"
                  placeholder=""
                  name="phoneNumber"
                  value={user.phoneNumber}
                  onChange={event =>
                    this.updateUser({ phoneNumber: event.target.value })
                  }
                  className={
                    user.phoneNumber !== this.props.user.phoneNumber
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                />
              </label>
              {fieldErrors.phoneNumber && (
                <em>{fieldErrors.phoneNumber.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.country}>
              <label>
                <span className="edit-user-modal__form-label">Country</span>
                <select
                  onChange={event => {
                    const newUser = { ...this.state.user };
                    newUser.country = event.target.value as string;
                    const countrySwitch =
                      newUser.country !== this.props.user.country;
                    const accounts = this.state.accounts;
                    const companyNumber = this.props.user.companyNumber;
                    newUser.createAccount = false;
                    if (countrySwitch) {
                      newUser.companyNumber = '';
                      newUser.companyName = '';
                    } else {
                      newUser.companyNumber = companyNumber || '';
                      newUser.companyName = this.props.user.companyName || '';
                    }
                    this.setState({
                      kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
                      accountIdSelected: this.props.user.accountId,
                      companyNameFromFetch: countrySwitch
                        ? ''
                        : this.props.user.companyName,
                      user: newUser,
                      reqCompanyNameFromFetch: ReqEnum.SUCCESS,
                    });
                    this.setState({
                      isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                        newUser,
                        companyNumber,
                        accounts
                      ),
                    });
                  }}
                  value={user.country}
                >
                  {Object.keys(supportedCountries).map(countryName => (
                    <option key={countryName}>
                      {supportedCountries[countryName].name}
                    </option>
                  ))}
                </select>
              </label>
            </EditFormGroupModal>
            <SwedenInfo country={this.state.user.country} />

            <div className={'edit-user-modal__form-group'}>
              <AccountsChooseOrCreateRadioButtons
                classNameSpan={EDIT_USER_FORM_LABEL_CLASS}
                classNameDiv={'edit-user-modal__form-radio normal'}
                checkedUseExistingAccount={
                  this.state.kindOfAccountSelection ===
                  KindOfAccountSelection.CHOOSE
                }
                checkedCreateNewAccount={
                  this.state.kindOfAccountSelection ===
                  KindOfAccountSelection.CREATE
                }
                onChangeChooseExistingAccount={() => {
                  const newUser = { ...this.state.user };
                  const countrySwitch =
                    newUser.country !== this.props.user.country;
                  const accounts = this.state.accounts;
                  const companyNumber = this.props.user.companyNumber;
                  newUser.createAccount = false;
                  if (countrySwitch) {
                    newUser.companyNumber = '';
                    newUser.companyName = '';
                  } else {
                    newUser.companyNumber = companyNumber || '';
                    newUser.companyName = this.props.user.companyName || '';
                  }
                  this.setState({
                    kindOfAccountSelection: KindOfAccountSelection.CHOOSE,
                    accountIdSelected: this.props.user.accountId,
                    companyNameFromFetch: countrySwitch
                      ? ''
                      : this.props.user.companyName,
                    user: newUser,
                    reqCompanyNameFromFetch: ReqEnum.SUCCESS,
                  });
                  this.setState({
                    isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                      newUser,
                      companyNumber,
                      accounts
                    ),
                  });
                }}
                onChangeChooseCreateNewAccount={() => {
                  const accounts = this.state.accounts;
                  const companyNumber = this.props.user.companyNumber;
                  const newUser = { ...this.state.user };
                  newUser.createAccount = true;
                  newUser.companyNumber = companyNumber || '';
                  newUser.companyName = this.props.user.companyName || '';
                  this.setState({
                    kindOfAccountSelection: KindOfAccountSelection.CREATE,
                    accountIdSelected: this.props.user.accountId,
                    companyNameFromFetch: this.props.user.companyName,
                    user: newUser,
                  });
                  this.setState({
                    isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
                      newUser,
                      companyNumber,
                      accounts
                    ),
                  });
                }}
              />
            </div>

            {this.state.kindOfAccountSelection ===
              KindOfAccountSelection.CHOOSE && (
              <AccountRow
                classNameSpan={EDIT_USER_FORM_LABEL_CLASS}
                classNameDiv={'edit-user-modal__form-radio normal'}
                fetchingAccounts={fetchingAccounts}
                accounts={this.state.accounts}
                updateUser={this.updateUserByAccount}
                accountId={this.props.user.accountId || -1}
                userToUpdate={this.props.user}
                newUser={this.state.user}
                filterForCountry={false}
              />
            )}

            <EditFormGroupModal hasError={!!fieldErrors.companyName}>
              <label>
                <span className="edit-user-modal__form-label">
                  Company name
                </span>
                {this.state.user.country !== 'Norway' && (
                  <input
                    type="txt"
                    name="companyName"
                    value={this.state.user.companyName}
                    onChange={event =>
                      this.updateUser({
                        companyName: event.target.value,
                      })
                    }
                    readOnly={
                      fetchingAccounts ||
                      this.state.kindOfAccountSelection ===
                        KindOfAccountSelection.CHOOSE
                    }
                  />
                )}
                {this.state.user.country === 'Norway' && (
                  <input
                    style={{
                      borderColor:
                        this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                          ? 'black'
                          : this.state.reqCompanyNameFromFetch ===
                            ReqEnum.PENDING
                          ? 'yellow'
                          : 'red',
                    }}
                    type="text"
                    data-lpignore="true"
                    placeholder=""
                    name="companyName"
                    value={
                      this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                        ? this.state.companyNameFromFetch.toString()
                        : this.state.reqCompanyNameFromFetch === ReqEnum.PENDING
                        ? 'make sure the company number is written correct'
                        : `Saved: ${this.state.user.companyName}, there is a network error to www.brreg.no/`
                    }
                    disabled={true}
                    className={
                      account && user.companyName !== account.name
                        ? EDIT_USER_MODIFIED_CLASS
                        : undefined
                    }
                    autoComplete="none"
                    readOnly={
                      fetchingAccounts ||
                      this.state.kindOfAccountSelection ===
                        KindOfAccountSelection.CHOOSE
                    }
                  />
                )}
              </label>
              {this.state.kindOfAccountSelection ===
                KindOfAccountSelection.CREATE && (
                <>
                  <ShowTestAccountIfItExists
                    isCompanyInTestAccountsList={
                      this.state.isCompanyInTestAccountsList
                    }
                    isCompanyInAccountsList={this.state.isCompanyInAccountsList}
                    isIdenticalCompanyNumber={
                      this.state.user.companyNumber ===
                      this.props.user.companyNumber
                    }
                    role={this.state.user.role}
                  />
                  {fieldErrors.companyName && (
                    <em>{fieldErrors.companyName.message}</em>
                  )}
                </>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal hasError={!!fieldErrors.companyNumber}>
              <label>
                <span className="edit-user-modal__form-label">
                  Organization number
                </span>
                <input
                  type="text"
                  data-lpignore="true"
                  placeholder=""
                  name="companyNumber"
                  value={user.companyNumber}
                  onChange={async event => {
                    const currentOrgNumber = event.target.value;

                    await this.getCompanyName(currentOrgNumber);

                    const companyNameToUpdate =
                      this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                        ? this.state.companyNameFromFetch
                        : this.state.user.companyName;

                    const updatedUserFields: Partial<EditUser> = {
                      companyNumber:
                        getOrgNumberStringAsString(currentOrgNumber),
                      companyName: companyNameToUpdate,
                    };

                    this.updateUser(updatedUserFields);
                  }}
                  className={
                    account && user.companyNumber !== account.companyNumber
                      ? EDIT_USER_MODIFIED_CLASS
                      : undefined
                  }
                  autoComplete="none"
                  readOnly={
                    fetchingAccounts ||
                    this.state.kindOfAccountSelection ===
                      KindOfAccountSelection.CHOOSE
                  }
                />
              </label>
              {fieldErrors.companyNumber && (
                <em>{fieldErrors.companyNumber.message}</em>
              )}
            </EditFormGroupModal>
            <EditFormGroupModal
              hasError={!!fieldErrors.paymentByInvoiceAllowed}
            >
              <label>
                <span className="edit-user-modal__form-label" />
                <div className="edit-user-modal__form-checkbox">
                  <input
                    type="checkbox"
                    name="paymentByInvoiceAllowed"
                    checked={user.paymentByInvoiceAllowed}
                    onChange={event =>
                      this.updateUser({
                        paymentByInvoiceAllowed: event.target.checked,
                      })
                    }
                    disabled={
                      this.state.kindOfAccountSelection ===
                      KindOfAccountSelection.CHOOSE
                    }
                  />
                  Payment by invoice allowed (Fakturakunde)
                </div>
              </label>
              {fieldErrors.paymentByInvoiceAllowed && (
                <em>{fieldErrors.paymentByInvoiceAllowed.message}</em>
              )}
            </EditFormGroupModal>
            <LockUser
              isLocked={user.isLocked}
              updateUserIsLockedMethod={this.updateUserIsLockedMethod}
            />
            <UpdateFeedback
              updatingUser={updatingUser}
              errors={errors}
              userUpdated={userUpdated}
            />
          </form>
        </div>
      </Modal>
    );
  }
}

export function SwedenInfo({ country }: { country?: string }): JSX.Element {
  return (
    <>
      {country === 'Sweden' && (
        <div className={'edit-user-modal__form-group'}>
          <label>
            <span className={EDIT_USER_FORM_LABEL_CLASS}>Help</span>
            <a
              href="https://snr4.bolagsverket.se/snrgate/startIn.do"
              target="_blank"
              rel="noopener noreferrer"
            >
              External search Swedish org number, use pattern with hyphen, e.g.
              "556091-2148"
            </a>
          </label>
        </div>
      )}
    </>
  );
}

export function UpdateFeedback({
  updatingUser,
  errors,
  userUpdated,
}: {
  updatingUser: boolean;
  errors: ErrorMsg[];
  userUpdated: boolean;
}): JSX.Element {
  return (
    <>
      <div className="edit-user-modal__form-submit">
        {updatingUser && (
          <div className="edit-user-modal__form-loader">
            <Loader type="bounce" size="medium" />
          </div>
        )}
      </div>
      {errors.length > 0 && (
        <div className="u-alert__error">
          {errors.map((error: ErrorMsg, i: number) => {
            return (
              <div key={`${error}-${i}`}>
                <div style={{ fontWeight: 500 }}>{error.message}</div>
              </div>
            );
          })}
        </div>
      )}
      {userUpdated && (
        <div className="u-alert__info">
          <p>The user has been updated successfully.</p>
        </div>
      )}
    </>
  );
}
